import {
  BundledDiscountPromoWithCalculated,
  BundledFreeGoodsPromoWithCalculated,
  Cart,
  CartTotalResponse,
  CartTotalResponseCompiledProductDiscount,
  DiscountPromoRecord,
  DiscountPromoWithCalculated,
  FreeGoodsCalculationResult,
  FreeGoodsPromoRecord,
  FreeGoodsPromoWithCalculated,
  OriginalFreeGoodsPromoRecord,
  PromoRecord,
} from '@diamond/shared/types';
import { calculateFreeGoods } from '@diamond/sol-buyer/utils';
import { D } from '@mobily/ts-belt';
import create from 'zustand';
import { persist } from 'zustand/middleware';

// Define product details type for local price calculation
type ProductDetail = {
  basePrice: number;
  discount_promos: Array<{
    id: string;
    type: 'PERCENTAGE' | 'FIXED_AMOUNT';
    value: number;
  }>;
};

type ProductDetailsRecord = Record<string, ProductDetail>;

type CartActions = {
  getCartItem: (cartId: string) => Cart;
  getCartEntries: () => Array<Cart>;
  getProduct: (productId: string) => Cart['product'];
  add: (product: Cart) => void;
  remove: (cartId: string) => void;
  reset: (cartItems: Array<Cart>) => void;
  setCompiledPromo: (cartTotalPromos: CartTotalResponse['promos']) => void;
  setFreeGoodsPromo: (cartTotalPromos: CartTotalResponse['promos']) => void;
  setDiscountPromo: (cartTotalPromos: CartTotalResponse['promos']) => void;
  setProductDetails: (productId: string, details: ProductDetail) => void;
  updateCartItemNote: (cartId: string, note: string) => void;
  updateCartItemQuantity: (cartId: string, quantity: number) => void;
  /**
   * Updates a cart item in the store by product ID.
   * Finds the cart item by product ID and updates it with the provided cart item data.
   * If the item is already selected, updates its quantity. If not selected, adds it.
   *
   * @param productId - The product ID to find and update
   * @param cartItem - The updated cart item data from API
   */
  updateCartItemByProductId: (productId: string, cartItem: Cart) => void;
  /**
   * Recalculate free goods promos for a product when quantity changes
   * Takes the original promo definitions and recalculates based on new quantity
   */
  recalculateFreeGoodsForProduct: (
    productId: string,
    newQuantity: number
  ) => void;
  queueAutoSelectProductId: (productId: string) => void;
  removeAutoSelectProductIds: (productIds: string[]) => void;
  /**
   * Clean up stale cart items that don't have corresponding entries in cartRecord
   * Returns the number of items cleaned up
   */
  cleanupStaleCartItems: () => number;
  /**
   * Deselect items from validation/checkout list without removing their data.
   * This preserves local quantity changes when user unchecks an item.
   */
  deselect: (cartIds: Array<string>) => void;
};

type CartRecord = Record<string, Cart>;
type ProductsRecord = Record<string, Cart['product']>;

type CartState = {
  cartItems: Array<string>;
  cartRecord: CartRecord;
  promoRecord: PromoRecord;
  freeGoodsPromoRecord: FreeGoodsPromoRecord;
  originalFreeGoodsPromos: OriginalFreeGoodsPromoRecord;
  discountPromoRecord: DiscountPromoRecord;
  productsRecord: ProductsRecord;
  productDetails: ProductDetailsRecord;
  pendingAutoSelectProductIds: string[];
};

/**
 * Cart Store for managing selected items (checklist) for checkout.
 *
 * This store maintains the user's selected items for checkout. It is separate from the Cart API,
 * which is the source of truth for all actual cart items. The store allows users to select/deselect
 * items in the cart before proceeding to checkout - only selected items are included in the order.
 *
 * Cart Store responsibilities:
 * - Store selected cart item IDs for checkout
 * - Maintain cart and product records for efficient lookups
 * - Store compiled promo information
 * - Manage product details for price calculations
 * - Store quantities for selected items (single source of truth for totals and checkout)
 *
 * Quantity Management:
 * - Quantities are synced to store when user checks/unchecks items (checkbox selection)
 * - Quantity sync happens immediately on checkbox selection, getting latest quantity from React Query cache
 * - API handles persistence separately (with cache invalidation)
 * - useCartTotal reads from store for total calculations
 * - Checkout uses getCartEntries() from store for order creation
 *
 * @remarks
 * - Store is single source of truth for checkout selection AND quantities
 * - Quantity sync happens immediately on checkbox selection
 * - API handles persistence separately (with cache invalidation)
 * - useCartTotal reads from store
 * - Checkout uses getCartEntries() from store
 */
export const useCartStore = create<CartState & CartActions>()(
  persist(
    (set, get) => ({
      cartItems: [],
      cartRecord: {},
      promoRecord: {},
      freeGoodsPromoRecord: {},
      originalFreeGoodsPromos: {},
      discountPromoRecord: {},
      productsRecord: {},
      productDetails: {},
      pendingAutoSelectProductIds: [],
      setCompiledPromo: (cartTotalPromos: CartTotalResponse['promos']) => {
        const promoEntryTemp: Array<
          Readonly<[string, CartTotalResponseCompiledProductDiscount]>
        > = [];

        cartTotalPromos.forEach((promoDivision) => {
          const promoCompiled = promoDivision.compiled_product_discounts
            .filter((promo) => promo.is_discount)
            .map((promo) => [promo.product_id, promo] as const);
          promoEntryTemp.push(...promoCompiled);
        });

        set(() => ({
          promoRecord: Object.fromEntries(promoEntryTemp),
        }));
      },
      setFreeGoodsPromo: (cartTotalPromos: CartTotalResponse['promos']) => {
        const freeGoodsPromoEntryTemp: Array<
          Readonly<
            [
              string,
              Array<
                | FreeGoodsPromoWithCalculated
                | BundledFreeGoodsPromoWithCalculated
              >
            ]
          >
        > = [];

        const originalFreeGoodsPromoEntryTemp: Array<
          Readonly<
            [
              string,
              Array<
                Omit<
                  FreeGoodsPromoWithCalculated,
                  keyof FreeGoodsCalculationResult
                > &
                  Omit<
                    BundledFreeGoodsPromoWithCalculated,
                    keyof FreeGoodsCalculationResult
                  >
              >
            ]
          >
        > = [];

        cartTotalPromos.forEach((promoDivision) => {
          const { single_free_good_promos, bundled_free_good_promos } =
            promoDivision;

          // Process single free goods promos
          if (single_free_good_promos?.length) {
            single_free_good_promos.forEach((promo) => {
              const productId = promo.ref_product_id || promo.product_id;
              const cartItem =
                get().cartRecord[
                  Object.keys(get().cartRecord).find(
                    (cartId) =>
                      get().cartRecord[cartId].product.id === productId
                  ) || ''
                ];

              if (cartItem) {
                const calculation = calculateFreeGoods({
                  userQuantity: cartItem.quantity,
                  minimumOrderQuantity: promo.minimum_order_quantity,
                  quantityFreeGoods: promo.quantity_free_goods || 0,
                  isAppliesInMultiples: promo.is_applies_in_multiples,
                });

                const existingPromos =
                  freeGoodsPromoEntryTemp.find(
                    ([id]) => id === productId
                  )?.[1] || [];
                const newPromo: FreeGoodsPromoWithCalculated = {
                  ...promo,
                  ...calculation,
                };
                freeGoodsPromoEntryTemp.push([
                  productId,
                  [...existingPromos, newPromo],
                ]);

                // Store original promo definition (without calculation results)
                const existingOriginalPromos =
                  originalFreeGoodsPromoEntryTemp.find(
                    ([id]) => id === productId
                  )?.[1] || [];
                const {
                  multiplier,
                  calculatedFreeQty,
                  totalFreeQtyUI,
                  ...originalPromo
                } = newPromo;
                originalFreeGoodsPromoEntryTemp.push([
                  productId,
                  [...existingOriginalPromos, originalPromo as any],
                ]);
              }
            });
          }

          // Process bundled free goods promos
          if (bundled_free_good_promos?.length) {
            bundled_free_good_promos.forEach((promo) => {
              // Find which cart item has this promo attached (from Cart API)
              // Cart API already has the correct product-promo mapping
              const matchingCartItem = Object.values(get().cartRecord).find(
                (item) => {
                  // Check if this cart item's product has this bundled promo
                  return item.product.bundled_free_good_promos?.some(
                    (productPromo) => productPromo.id === promo.id
                  );
                }
              );

              if (matchingCartItem) {
                // Use the correct purchased product ID (not the free goods product ID)
                const productId = matchingCartItem.product.id;

                const calculation = calculateFreeGoods({
                  userQuantity: matchingCartItem.quantity,
                  minimumOrderQuantity: promo.sum_minimum_order_quantity,
                  quantityFreeGoods: promo.quantity_free_goods || 0,
                  isAppliesInMultiples: false, // Bundled promos typically don't apply in multiples
                });

                const existingPromos =
                  freeGoodsPromoEntryTemp.find(
                    ([id]) => id === productId
                  )?.[1] || [];
                const newPromo: BundledFreeGoodsPromoWithCalculated = {
                  ...promo,
                  ...calculation,
                };
                freeGoodsPromoEntryTemp.push([
                  productId,
                  [...existingPromos, newPromo],
                ]);

                // Store original promo definition (without calculation results)
                const existingOriginalPromos =
                  originalFreeGoodsPromoEntryTemp.find(
                    ([id]) => id === productId
                  )?.[1] || [];
                const {
                  multiplier,
                  calculatedFreeQty,
                  totalFreeQtyUI,
                  ...originalPromo
                } = newPromo;
                originalFreeGoodsPromoEntryTemp.push([
                  productId,
                  [...existingOriginalPromos, originalPromo as any],
                ]);
              }
            });
          }
        });

        set(() => ({
          freeGoodsPromoRecord: Object.fromEntries(freeGoodsPromoEntryTemp),
          originalFreeGoodsPromos: Object.fromEntries(
            originalFreeGoodsPromoEntryTemp
          ),
        }));
      },
      setDiscountPromo: (cartTotalPromos: CartTotalResponse['promos']) => {
        const discountPromoEntryTemp: Array<
          Readonly<
            [
              string,
              Array<
                DiscountPromoWithCalculated | BundledDiscountPromoWithCalculated
              >
            ]
          >
        > = [];

        cartTotalPromos.forEach((promoDivision) => {
          const { single_discount_promos, bundled_discount_promos } =
            promoDivision;

          // Process single discount promos
          if (single_discount_promos?.length) {
            single_discount_promos.forEach((promo) => {
              const productId = promo.product_id;
              const existingPromos =
                discountPromoEntryTemp.find(([id]) => id === productId)?.[1] ||
                [];
              const newPromo: DiscountPromoWithCalculated = {
                ...promo,
              };
              discountPromoEntryTemp.push([
                productId,
                [...existingPromos, newPromo],
              ]);
            });
          }

          // Process bundled discount promos
          if (bundled_discount_promos?.length) {
            bundled_discount_promos.forEach((promo) => {
              // Bundled discount promos don't have a specific product_id
              // They apply to multiple products, so we'll skip them for now
              // as they don't fit the per-product promo display pattern
            });
          }
        });

        set(() => ({
          discountPromoRecord: Object.fromEntries(discountPromoEntryTemp),
        }));
      },
      getCartItem: (cartId) => get().cartRecord[cartId],
      getCartEntries: () =>
        get()
          .cartItems.map((cartId) => get().getCartItem(cartId))
          .filter((entry) => entry !== null && entry !== undefined),
      getProduct: (productId) => get().productsRecord[productId],
      add: (cartItem) => {
        const quantity =
          cartItem.quantity ?? cartItem.product.minimum_order_quantity ?? 1000;
        const itemWithQuantity = { ...cartItem, quantity };
        return set((state) => ({
          cartRecord: D.set(
            state.cartRecord,
            itemWithQuantity.id,
            itemWithQuantity
          ),
          cartItems: [...state.cartItems, itemWithQuantity.id],
          productsRecord: D.set(
            state.productsRecord,
            itemWithQuantity.product.id,
            itemWithQuantity.product
          ),
        }));
      },
      remove: (cartItemId) => {
        if (cartItemId in get().cartRecord) {
          set((state) => {
            const productId = state.cartRecord[cartItemId].product.id;
            return {
              cartItems: state.cartItems.filter((id) => id !== cartItemId),
              cartRecord: D.deleteKey(state.cartRecord, cartItemId),
              productsRecord: D.deleteKey(state.productsRecord, productId),
            };
          });
        }
      },
      /**
       * Deselects items from the active checkout selection (`cartItems`).
       *
       * CRITICAL DISTINCTION FROM `remove`:
       * - `remove`: Completely deletes the item from the store (`cartRecord`), losing any local state like updated quantities.
       * - `deselect`: ONLY removes the ID from the `cartItems` list. The item's data remains in `cartRecord`.
       *
       * This was added to fix a bug where unchecking a cart item would reset its quantity to the API default
       * because the local state was being destroyed by `remove`.
       */
      deselect: (cartIds) => {
        const idsToRemove = new Set(cartIds);
        set((state) => ({
          cartItems: state.cartItems.filter((id) => !idsToRemove.has(id)),
        }));
      },
      reset: (cartItems) => {
        const cartItemsWithQuantity = cartItems.map((cart) => {
          const quantity =
            cart.quantity ?? cart.product.minimum_order_quantity ?? 1000;
          return { ...cart, quantity };
        });
        return set({
          cartRecord: Object.fromEntries(
            cartItemsWithQuantity.map((cart) => [cart.id, cart])
          ),
          cartItems: cartItemsWithQuantity.map((c) => c.id),
          productsRecord: Object.fromEntries(
            cartItemsWithQuantity.map((cart) => [cart.product.id, cart.product])
          ),
        });
      },
      setProductDetails: (productId, details) =>
        set((state) => ({
          productDetails: {
            ...state.productDetails,
            [productId]: details,
          },
        })),
      updateCartItemNote: (cartId, note) =>
        set((state) => {
          const currentEntry = state.cartRecord[cartId];
          if (!currentEntry) return {};

          return {
            cartRecord: {
              ...state.cartRecord,
              [cartId]: {
                ...currentEntry,
                note,
              },
            },
          };
        }),
      /**
       * Recalculate free goods promos for a product when quantity changes.
       * Uses original promo definitions to recalculate based on new quantity.
       *
       * @param productId - The product ID
       * @param newQuantity - The new quantity (magnified value)
       */
      recalculateFreeGoodsForProduct: (productId, newQuantity) =>
        set((state) => {
          const originalPromos = state.originalFreeGoodsPromos[productId];
          if (!originalPromos || originalPromos.length === 0) {
            return {};
          }

          // Recalculate each promo with the new quantity
          const recalculatedPromos = originalPromos.map((promo) => {
            // Determine which minimum order quantity to use (single vs bundled)
            const minimumOrderQty =
              'sum_minimum_order_quantity' in promo
                ? (promo as any).sum_minimum_order_quantity
                : (promo as any).minimum_order_quantity;

            // Determine if applies in multiples
            const isAppliesInMultiples =
              'is_applies_in_multiples' in promo
                ? (promo as any).is_applies_in_multiples
                : false;

            // Recalculate using the calculateFreeGoods utility
            const calculation = calculateFreeGoods({
              userQuantity: newQuantity,
              minimumOrderQuantity: minimumOrderQty,
              quantityFreeGoods: (promo as any).quantity_free_goods || 0,
              isAppliesInMultiples: isAppliesInMultiples,
            });

            // Return promo with updated calculation
            return {
              ...promo,
              ...calculation,
            };
          });

          return {
            freeGoodsPromoRecord: {
              ...state.freeGoodsPromoRecord,
              [productId]: recalculatedPromos,
            },
          };
        }),
      /**
       * Updates the quantity of a cart item in the store.
       * This is used internally when syncing quantity on checkbox selection.
       * Also triggers recalculation of free goods promos for this product.
       *
       * @param cartId - The cart item ID
       * @param quantity - The new quantity (magnified value)
       */
      updateCartItemQuantity: (cartId, quantity) =>
        set((state) => {
          const currentEntry = state.cartRecord[cartId];
          const nextCartRecord = currentEntry
            ? {
                ...state.cartRecord,
                [cartId]: {
                  ...currentEntry,
                  quantity,
                },
              }
            : state.cartRecord;

          // Get product ID to trigger recalculation
          const productId = currentEntry?.product.id;

          // Recalculate free goods promos if product has them
          let updatedFreeGoodsPromos = state.freeGoodsPromoRecord;
          if (productId && state.originalFreeGoodsPromos[productId]) {
            const originalPromos = state.originalFreeGoodsPromos[productId];
            const recalculatedPromos = originalPromos.map((promo) => {
              const minimumOrderQty =
                'sum_minimum_order_quantity' in promo
                  ? (promo as any).sum_minimum_order_quantity
                  : (promo as any).minimum_order_quantity;

              const isAppliesInMultiples =
                'is_applies_in_multiples' in promo
                  ? (promo as any).is_applies_in_multiples
                  : false;

              const calculation = calculateFreeGoods({
                userQuantity: quantity,
                minimumOrderQuantity: minimumOrderQty,
                quantityFreeGoods: (promo as any).quantity_free_goods || 0,
                isAppliesInMultiples: isAppliesInMultiples,
              });

              return {
                ...promo,
                ...calculation,
              };
            });

            updatedFreeGoodsPromos = {
              ...state.freeGoodsPromoRecord,
              [productId]: recalculatedPromos,
            };
          }

          return {
            cartRecord: nextCartRecord,
            freeGoodsPromoRecord: updatedFreeGoodsPromos,
          };
        }),
      /**
       * Updates a cart item in the store by product ID.
       * Finds the cart item by product ID and updates it with the provided cart item data.
       * If the item is already selected, updates its quantity. If not selected, adds it.
       *
       * @param productId - The product ID to find and update
       * @param cartItem - The updated cart item data from API
       */
      updateCartItemByProductId: (productId, cartItem) =>
        set((state) => {
          // Find existing cart item by product ID
          const existingCartId = Object.keys(state.cartRecord).find(
            (cartId) => state.cartRecord[cartId].product.id === productId
          );

          if (existingCartId) {
            // Item exists - update it
            const currentEntry = state.cartRecord[existingCartId];
            const updatedEntry = {
              ...cartItem,
              quantity: cartItem.quantity ?? currentEntry.quantity,
            };

            // Update cartRecord
            const nextCartRecord = {
              ...state.cartRecord,
              [existingCartId]: updatedEntry,
            };

            // Update productsRecord
            const nextProductsRecord = {
              ...state.productsRecord,
              [productId]: cartItem.product,
            };

            // Recalculate free goods promos if product has them
            let updatedFreeGoodsPromos = state.freeGoodsPromoRecord;
            if (state.originalFreeGoodsPromos[productId]) {
              const originalPromos = state.originalFreeGoodsPromos[productId];
              const recalculatedPromos = originalPromos.map((promo) => {
                const minimumOrderQty =
                  'sum_minimum_order_quantity' in promo
                    ? (promo as any).sum_minimum_order_quantity
                    : (promo as any).minimum_order_quantity;

                const isAppliesInMultiples =
                  'is_applies_in_multiples' in promo
                    ? (promo as any).is_applies_in_multiples
                    : false;

                const calculation = calculateFreeGoods({
                  userQuantity: updatedEntry.quantity,
                  minimumOrderQuantity: minimumOrderQty,
                  quantityFreeGoods: (promo as any).quantity_free_goods || 0,
                  isAppliesInMultiples: isAppliesInMultiples,
                });

                return {
                  ...promo,
                  ...calculation,
                };
              });

              updatedFreeGoodsPromos = {
                ...state.freeGoodsPromoRecord,
                [productId]: recalculatedPromos,
              };
            }

            return {
              cartRecord: nextCartRecord,
              productsRecord: nextProductsRecord,
              freeGoodsPromoRecord: updatedFreeGoodsPromos,
            };
          }

          // Item doesn't exist yet - no-op (should be handled by add() separately)
          return {};
        }),
      queueAutoSelectProductId: (productId) =>
        set((state) => {
          if (!productId) {
            return {};
          }

          const filtered = state.pendingAutoSelectProductIds.filter(
            (id) => id !== productId
          );

          return {
            pendingAutoSelectProductIds: [...filtered, productId],
          };
        }),
      removeAutoSelectProductIds: (productIds) =>
        set((state) => {
          if (!productIds.length) {
            return {};
          }

          const removalSet = new Set(productIds);
          return {
            pendingAutoSelectProductIds:
              state.pendingAutoSelectProductIds.filter(
                (id) => !removalSet.has(id)
              ),
          };
        }),
      cleanupStaleCartItems: () => {
        const state = get();
        const validCartItems = state.cartItems.filter(
          (cartId) => cartId in state.cartRecord
        );
        const cleanedCount = state.cartItems.length - validCartItems.length;

        if (cleanedCount > 0) {
          set({
            cartItems: validCartItems,
          });

          console.info(
            `[CartStore] Cleaned up ${cleanedCount} stale cart item(s)`,
            {
              staleIds: state.cartItems.filter(
                (id) => !(id in state.cartRecord)
              ),
            }
          );
        }

        return cleanedCount;
      },
    }),
    {
      name: 'cart-storage',
      merge: (
        persistedState: unknown,
        currentState: CartState & CartActions
      ) => {
        const state = persistedState as Partial<CartState>;

        // Validate and cleanup stale cart items
        const cartRecord =
          state?.cartRecord && typeof state.cartRecord === 'object'
            ? state.cartRecord
            : {};

        const cartItems = Array.isArray(state?.cartItems)
          ? state.cartItems
          : [];

        // Remove cart IDs that don't have corresponding entries in cartRecord
        const validCartItems = cartItems.filter(
          (cartId) => cartId in cartRecord
        );

        // Log cleanup if stale data detected
        if (validCartItems.length !== cartItems.length) {
          const staleCount = cartItems.length - validCartItems.length;
          console.warn(
            `[CartStore] Removed ${staleCount} stale cart item(s) from localStorage`,
            {
              total: cartItems.length,
              valid: validCartItems.length,
              staleIds: cartItems.filter((id) => !(id in cartRecord)),
            }
          );
        }

        return {
          ...currentState,
          cartItems: validCartItems,
          cartRecord,
          productsRecord:
            state?.productsRecord && typeof state.productsRecord === 'object'
              ? state.productsRecord
              : {},
        };
      },
    }
  )
);
